import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import {
  buildAnswerSummary,
  buildProposalEmailBody,
  recommendTier,
  type Answers,
} from "./questions";

const CAL_URL = "https://www.cal.eu/andreacab/holymoly-free-call?user=andreacab";
const HELLO_EMAIL = "hello@holymoly.dev";

interface FitResultProps {
  answers: Answers;
  inverted: boolean;
  fadeColor: string;
  onBack: () => void;
}

export function FitResult({ answers, inverted, fadeColor, onBack }: FitResultProps) {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const tier = recommendTier(answers, t);
  const calHref = `${CAL_URL}&notes=${encodeURIComponent(buildAnswerSummary(answers, tier, t))}`;
  const proposalSubject = tier.tier
    ? t("fit.proposalEmail.subjectWithTier", { tier: tier.tier })
    : t("fit.proposalEmail.subject");
  const proposalHref = `mailto:${HELLO_EMAIL}?subject=${encodeURIComponent(
    proposalSubject,
  )}&body=${encodeURIComponent(buildProposalEmailBody(answers, tier, t))}`;

  return (
    <>
      <span className={cn("eyebrow", fadeColor, inverted ? "text-page-bg/70" : "")}>
        {t("fit.resultLabel")}
      </span>
      <h1
        className={cn(
          "mt-4 text-[clamp(34px,5.4vw,60px)] leading-[1.05] font-medium tracking-[-0.035em]",
          fadeColor,
          inverted ? "text-page-bg" : "text-ink",
        )}
      >
        {tier.tier ? t("fit.result.titleFit") : t("fit.result.titleNoFit")}
      </h1>

      {tier.tier && (
        <div
          className={cn(
            "mt-6 inline-flex items-center gap-3 rounded-full border px-4 py-2 backdrop-blur",
            fadeColor,
            inverted ? "border-page-bg/30 bg-ink/30" : "border-ink/15 bg-surface/65",
          )}
        >
          <span
            className={cn(
              "mono text-[10px] tracking-[0.18em] uppercase",
              inverted ? "text-page-bg/65" : "text-ink-3",
            )}
          >
            {t("fit.result.recommended")}
          </span>
          <span
            className={cn(
              "text-[15px] font-medium tracking-tight",
              inverted ? "text-page-bg" : "text-ink",
            )}
          >
            {tier.tier}
          </span>
        </div>
      )}

      <p
        className={cn(
          "mx-auto mt-5 max-w-[58ch] text-[clamp(15px,1.4vw,17px)] leading-[1.55]",
          fadeColor,
          inverted ? "text-page-bg/85" : "text-ink-2",
        )}
      >
        {tier.pitch}
      </p>

      <div className="mt-10 grid grid-cols-1 gap-3 text-left md:grid-cols-3">
        <ResultCard
          inverted={inverted}
          fadeColor={fadeColor}
          primary
          href={calHref}
          target="_blank"
          eyebrow={t("fit.result.cards.call.eyebrow")}
          title={t("fit.result.cards.call.title")}
          body={t("fit.result.cards.call.body")}
        />
        <ResultCard
          inverted={inverted}
          fadeColor={fadeColor}
          href={proposalHref}
          eyebrow={t("fit.result.cards.proposal.eyebrow")}
          title={t("fit.result.cards.proposal.title")}
          body={t("fit.result.cards.proposal.body")}
        />
        <ResultCard
          inverted={inverted}
          fadeColor={fadeColor}
          href={localizedHref("/work")}
          eyebrow={t("fit.result.cards.work.eyebrow")}
          title={t("fit.result.cards.work.title")}
          body={t("fit.result.cards.work.body")}
        />
      </div>

      <div className="mt-10 flex items-center justify-center">
        <button
          type="button"
          onClick={onBack}
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-medium tracking-tight backdrop-blur transition-colors",
            inverted
              ? "border-page-bg/30 bg-ink/30 text-page-bg hover:bg-ink/40"
              : "border-ink/15 bg-surface/60 text-ink hover:bg-surface",
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
          {t("fit.reviseAnswers")}
        </button>
      </div>
    </>
  );
}

interface ResultCardProps {
  href: string;
  target?: "_blank";
  primary?: boolean;
  eyebrow: string;
  title: string;
  body: string;
  inverted: boolean;
  fadeColor: string;
}

function ResultCard({
  href,
  target,
  primary,
  eyebrow,
  title,
  body,
  inverted,
  fadeColor,
}: ResultCardProps) {
  const rel = target === "_blank" ? "noopener noreferrer" : undefined;
  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={cn(
        "group block rounded-2xl border p-5 backdrop-blur transition-all duration-200 hover:-translate-y-0.5",
        primary
          ? inverted
            ? "border-page-bg bg-page-bg text-ink shadow-lg shadow-black/30 hover:bg-white"
            : "border-ink bg-ink text-page-bg shadow-lg shadow-black/15 hover:bg-[#000]"
          : inverted
            ? "border-page-bg/30 bg-ink/30 text-page-bg hover:bg-ink/45"
            : "border-ink/15 bg-surface/65 text-ink hover:bg-surface",
        fadeColor,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            "mono text-[10px] tracking-[0.18em] uppercase",
            primary ? "opacity-70" : inverted ? "text-page-bg/65" : "text-ink-3",
          )}
        >
          {eyebrow}
        </div>
        <ArrowUpRight
          className={cn(
            "h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
            primary ? "opacity-80" : "opacity-50",
          )}
          strokeWidth={2.25}
        />
      </div>
      <div className="mt-3 text-[18px] leading-[1.25] font-medium tracking-[-0.02em]">{title}</div>
      <div
        className={cn(
          "mt-2 text-[13px] leading-[1.5]",
          primary ? "opacity-80" : inverted ? "text-page-bg/75" : "text-ink-2",
        )}
      >
        {body}
      </div>
    </a>
  );
}
